import { payDateListInMonth, parsePayPeriodParams } from '../engine/cashPolicy';
import { daysInMonth, monthKeyFromT } from '../engine/calendar';
import { impliedDriverInput, type DriverContext } from '../engine/drivers';
import type {
    CaseInputs,
    DriverType,
    History,
    Model,
    SchemaEntry,
    View,
} from '../engine/types';
import { driverInputUnit } from '../driverUtils';
import { labelCellKey, methodologyCellKey, projectedCellKey } from '../cellKeys';
import { explainRecipe } from '../caseJson/recipe';
import { resolveSeasonalityFactors } from '../caseJson/recipeHistory';
import { projectionMonthKeys } from '../caseJson/utils';
import type { ExplainRecipeBlock } from '../caseJson/recipe';
import {
    formatCurrency,
    formatMonthLabel,
    formatNumber,
    formatPayDateLabel,
    formatPercent,
} from '../projectionsFormat';

const VIEW_LABELS: Record<View, string> = {
    river_oaks: 'River Oaks',
    highland_park: 'Highland Park',
    ga: 'G&A',
    consolidated: 'Consolidated',
};

const FIXED_NO_CONTEXT = new Set<DriverType>([
    'orders_x_ticket',
    'linked',
    'plug',
    'carry_forward',
]);

const WEIGHTED_TRAILING_DRIVERS = new Set<DriverType>([
    'pct_gross_sales',
    'pct_of_line',
    'dollar_per_line_unit',
    'count_per_open_day',
    'dollar_per_open_day',
    'dollar_per_pay_period',
    'dollar_per_sq_ft',
    'dollar_per_1000_sqft_calendar_day',
    'dollar_per_fte',
    'dio',
    'dso',
    'dpo',
    'count_per_calendar_day',
    'dollar_per_calendar_day',
]);

const GPS_KEY = 'is.gross_product_sales';
const ORDERS_KEY = 'is.total_orders';
const TICKET_KEY = 'is.avg_ticket';
const SQFT_KEY = 'is.square_footage';
const DAYS_OPEN_KEY = 'is.days_open_for_business';
const BOH_KEY = 'is.boh';
const FOH_KEY = 'is.foh';
const TOTAL_COGS_KEY = 'is.total_cogs';
const NET_REVENUE_KEY = 'is.net_revenue';

export interface ExplainModel {
    header: string;
    computed: { formula: string; parts: { label: string; value_rendered: string }[] };
    recipe: ExplainRecipeBlock | null;
    context: {
        last_actual: string | null;
        same_month_last_year: string | null;
        trailing_12: string | null;
        projected_12: string | null;
        rank_note: string | null;
    } | null;
    rationale: string | null;
    notes: { cell: string; text: string }[];
}

export interface ExplainCellInput {
    view: View;
    lineKey: string;
    t: number;
    schema: SchemaEntry[];
    inputs: CaseInputs;
    history: History | null;
    evaluated: Model | null;
    notes: Record<string, string>;
    lastClosedMonth: string;
}

function schemaLabel(schema: SchemaEntry[], lineKey: string): string {
    return schema.find((e) => e.key === lineKey)?.label.trim() ?? lineKey;
}

function historyView(view: View, lineKey: string, inputs: CaseInputs): View {
    if (inputs.consolidated_only.includes(lineKey) || view === 'consolidated') {
        return 'consolidated';
    }
    return view;
}

function getLineValue(
    evaluated: Model | null,
    history: History | null,
    view: View,
    lineKey: string,
    monthT: number,
): number | null {
    if (monthT <= 0 && history) {
        const idx = history.nHistory - 1 + monthT;
        const series = history.values[view]?.[lineKey];
        if (series && idx >= 0 && idx < series.length) {
            const raw = series[idx];
            return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
        }
    }
    const raw = evaluated?.values[view]?.[lineKey]?.[monthT] ?? null;
    return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

function driverInputAt(inputs: CaseInputs, view: View, lineKey: string, monthT: number): number | null {
    const lineState = inputs.drivers[view]?.[lineKey];
    const active = lineState?.active;
    if (!active) {
        return null;
    }
    const values = lineState.by_type[active]?.values;
    if (!values || monthT < 1 || monthT > 36) {
        return null;
    }
    return values[monthT - 1] ?? null;
}

function buildCtx(input: ExplainCellInput): DriverContext {
    const { view, lineKey, inputs, history, evaluated, lastClosedMonth } = input;
    const histView = historyView(view, lineKey, inputs);
    return {
        getNumeric: (v, key, monthT) => getLineValue(evaluated, history, v === 'consolidated' ? 'consolidated' : v, key, monthT),
        daysInMonth: (monthT) => daysInMonth(lastClosedMonth, monthT),
        monthKey: (monthT) => monthKeyFromT(lastClosedMonth, monthT),
        schemaByKey: Object.fromEntries(input.schema.map((e) => [e.key, e])),
        driverInput: (v, key, monthT) => driverInputAt(inputs, v, key, monthT),
        driverParams: (v, key) => {
            const lineActive = inputs.drivers[v]?.[key]?.active;
            if (!lineActive) {
                return undefined;
            }
            return inputs.drivers[v]?.[key]?.by_type[lineActive]?.params;
        },
        addDiagnostic: () => {},
        view: histView,
    };
}

function formatInput(driverType: DriverType, value: number | null): string {
    if (value == null) {
        return '—';
    }
    const unit = driverInputUnit(driverType as Parameters<typeof driverInputUnit>[0]);
    if (unit === 'pct') {
        return formatPercent(value);
    }
    if (unit === 'count') {
        return formatNumber(value);
    }
    return formatCurrency(value);
}

function formatContextLine(
    driverType: DriverType,
    inputVal: number | null,
    usdVal: number | null,
    showUsd: boolean,
): string | null {
    if (inputVal == null) {
        return null;
    }
    const inputPart = formatInput(driverType, inputVal);
    if (showUsd && usdVal != null) {
        return `${inputPart} (${formatCurrency(usdVal)})`;
    }
    return inputPart;
}

function ordinal(n: number): string {
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) {
        return `${n}th`;
    }
    const mod10 = n % 10;
    if (mod10 === 1) {
        return `${n}st`;
    }
    if (mod10 === 2) {
        return `${n}nd`;
    }
    if (mod10 === 3) {
        return `${n}rd`;
    }
    return `${n}th`;
}

function buildRankNote(
    history: History,
    histView: View,
    lineKey: string,
    lastClosedMonth: string,
    explainedT: number,
): string | null {
    const smlyT = explainedT - 12;
    const smlyVal = getLineValue(null, history, histView, lineKey, smlyT);
    if (smlyVal == null) {
        return null;
    }
    const smlyMonth = monthKeyFromT(lastClosedMonth, smlyT);
    const last12: Array<{ t: number; val: number }> = [];
    for (let t = -11; t <= 0; t += 1) {
        const v = getLineValue(null, history, histView, lineKey, t);
        if (v != null) {
            last12.push({ t, val: v });
        }
    }
    if (last12.length === 0) {
        return null;
    }
    const sorted = [...last12].sort((a, b) => b.val - a.val);
    const rank =
        sorted.findIndex((item) => item.t === smlyT) >= 0
            ? sorted.findIndex((item) => item.val === smlyVal)
            : sorted.filter((item) => item.val > smlyVal).length;
    const tiedAtRank = sorted.filter((item) => item.val === smlyVal).length > 1;
    const label = formatMonthLabel(smlyMonth);
    if (rank === 0) {
        return tiedAtRank
            ? `${label} tied for the highest of the last 12 actual months`
            : `${label} was the highest of the last 12 actual months`;
    }
    if (rank === last12.length - 1) {
        return tiedAtRank
            ? `${label} tied for the lowest of the last 12 actual months`
            : `${label} was the lowest of the last 12 actual months`;
    }
    const ord = ordinal(rank + 1);
    return tiedAtRank
        ? `${label} tied for the ${ord}-highest of the last 12 actual months`
        : `${label} was the ${ord}-highest of the last 12 actual months`;
}

function trailingImplied(
    ctx: DriverContext,
    driverType: DriverType,
    view: View,
    lineKey: string,
    history: History,
    lastClosedMonth: string,
): number | null {
    const histView = view;
    if (WEIGHTED_TRAILING_DRIVERS.has(driverType)) {
        let numSum = 0;
        let denSum = 0;
        for (let t = -11; t <= 0; t += 1) {
            const lineVal = getLineValue(null, history, histView, lineKey, t);
            if (lineVal == null) {
                continue;
            }
            if (driverType === 'pct_gross_sales') {
                const gps = getLineValue(null, history, histView, GPS_KEY, t);
                if (gps == null || gps === 0) {
                    continue;
                }
                numSum += lineVal;
                denSum += gps;
            } else if (driverType === 'pct_of_line' || driverType === 'dollar_per_line_unit') {
                const ref = ctx.driverParams(view, lineKey)?.ref;
                if (!ref) {
                    continue;
                }
                const refVal = getLineValue(null, history, histView, ref, t);
                if (refVal == null || refVal === 0) {
                    continue;
                }
                numSum += lineVal;
                denSum += refVal;
            } else if (
                driverType === 'count_per_open_day' ||
                driverType === 'dollar_per_open_day'
            ) {
                const daysOpen = getLineValue(null, history, histView, DAYS_OPEN_KEY, t);
                if (daysOpen == null || daysOpen === 0) {
                    continue;
                }
                numSum += lineVal;
                denSum += daysOpen;
            } else if (driverType === 'dollar_per_pay_period') {
                const payParams = parsePayPeriodParams(ctx.driverParams(view, lineKey));
                if (!payParams) {
                    continue;
                }
                const mk = monthKeyFromT(lastClosedMonth, t);
                const n = payDateListInMonth(payParams.cadence, payParams.anchor_date, mk).length;
                if (n === 0) {
                    continue;
                }
                numSum += lineVal;
                denSum += n;
            } else if (driverType === 'dollar_per_sq_ft') {
                const sqft = getLineValue(null, history, histView, SQFT_KEY, t);
                if (sqft == null || sqft === 0) {
                    continue;
                }
                numSum += lineVal;
                denSum += sqft;
            } else if (driverType === 'dollar_per_1000_sqft_calendar_day') {
                const sqft = getLineValue(null, history, histView, SQFT_KEY, t);
                const days = daysInMonth(lastClosedMonth, t);
                if (sqft == null || sqft === 0 || days === 0) {
                    continue;
                }
                numSum += lineVal;
                denSum += (sqft * days) / 1000;
            } else if (driverType === 'dollar_per_fte') {
                const boh = getLineValue(null, history, histView, BOH_KEY, t) ?? 0;
                const foh = getLineValue(null, history, histView, FOH_KEY, t) ?? 0;
                const hourly = boh + foh;
                const salaried =
                    histView === 'ga'
                        ? (getLineValue(null, history, histView, 'is.salaried_headcount', t) ?? 0)
                        : 0;
                const fte = hourly > 0 ? hourly : salaried;
                if (fte === 0) {
                    continue;
                }
                numSum += lineVal;
                denSum += fte;
            } else if (driverType === 'count_per_calendar_day' || driverType === 'dollar_per_calendar_day') {
                const days = daysInMonth(lastClosedMonth, t);
                if (days === 0) {
                    continue;
                }
                numSum += lineVal;
                denSum += days;
            } else if (driverType === 'dio' || driverType === 'dpo') {
                const cogs = getLineValue(null, history, 'consolidated', TOTAL_COGS_KEY, t);
                const days = daysInMonth(lastClosedMonth, t);
                if (cogs == null || cogs === 0 || days === 0) {
                    continue;
                }
                numSum += lineVal * days;
                denSum += cogs;
            } else if (driverType === 'dso') {
                const nr = getLineValue(null, history, 'consolidated', NET_REVENUE_KEY, t);
                const days = daysInMonth(lastClosedMonth, t);
                if (nr == null || nr === 0 || days === 0) {
                    continue;
                }
                numSum += lineVal * days;
                denSum += nr;
            }
        }
        return denSum === 0 ? null : numSum / denSum;
    }
    const vals: number[] = [];
    for (let t = -11; t <= 0; t += 1) {
        const implied = impliedDriverInput(driverType, ctx, histView, lineKey, t);
        if (implied != null) {
            vals.push(implied);
        }
    }
    if (vals.length === 0) {
        return null;
    }
    return vals.reduce((s, v) => s + v, 0) / vals.length;
}

function buildComputed(
    driverType: DriverType,
    ctx: DriverContext,
    view: View,
    lineKey: string,
    t: number,
    lastClosedMonth: string,
    schema: SchemaEntry[],
): { formula: string; parts: { label: string; value_rendered: string }[] } {
    const computed = ctx.getNumeric(view, lineKey, t);
    const input = ctx.driverInput(view, lineKey, t);
    const parts: { label: string; value_rendered: string }[] = [];

    switch (driverType) {
        case 'count_value':
        case 'dollar_value':
            return {
                formula: formatInput(driverType, input),
                parts: [{ label: 'Value', value_rendered: formatInput(driverType, input) }],
            };
        case 'count_per_calendar_day': {
            const days = ctx.daysInMonth(t);
            const formula = `${formatInput(driverType, input)} × ${days} days in month = ${formatNumber(computed)}`;
            return { formula, parts: [{ label: 'Days', value_rendered: String(days) }] };
        }
        case 'dollar_per_calendar_day': {
            const days = ctx.daysInMonth(t);
            const formula = `${formatInput(driverType, input)} × ${days} days in month = ${formatCurrency(computed)}`;
            return { formula, parts: [{ label: 'Days', value_rendered: String(days) }] };
        }
        case 'count_per_open_day': {
            const daysOpen = ctx.getNumeric(view, DAYS_OPEN_KEY, t);
            const formula = `${formatInput(driverType, input)} × days open ${daysOpen ?? '—'} = ${formatNumber(computed)}`;
            return { formula, parts: [{ label: 'Days open', value_rendered: formatNumber(daysOpen) }] };
        }
        case 'dollar_per_open_day': {
            const daysOpen = ctx.getNumeric(view, DAYS_OPEN_KEY, t);
            const formula = `${formatInput(driverType, input)} × days open ${daysOpen ?? '—'} = ${formatCurrency(computed)}`;
            return { formula, parts: [{ label: 'Days open', value_rendered: formatNumber(daysOpen) }] };
        }
        case 'dollar_per_pay_period': {
            const payParams = parsePayPeriodParams(ctx.driverParams(view, lineKey));
            const mk = monthKeyFromT(lastClosedMonth, t);
            const dates = payParams
                ? payDateListInMonth(payParams.cadence, payParams.anchor_date, mk)
                : [];
            const dateLabels = dates.map(formatPayDateLabel).join(', ');
            const formula = `${formatInput(driverType, input)} × ${dates.length} pay dates (${dateLabels}) = ${formatCurrency(computed)}`;
            return { formula, parts: [{ label: 'Pay dates', value_rendered: dateLabels }] };
        }
        case 'dollar_per_sq_ft': {
            const sqft = ctx.getNumeric(view, SQFT_KEY, t);
            const formula = `${formatInput(driverType, input)} × ${formatNumber(sqft)} sq ft = ${formatCurrency(computed)}`;
            return { formula, parts: [{ label: 'Sq ft', value_rendered: formatNumber(sqft) }] };
        }
        case 'dollar_per_1000_sqft_calendar_day': {
            const sqft = ctx.getNumeric(view, SQFT_KEY, t);
            const days = ctx.daysInMonth(t);
            const formula = `${formatInput(driverType, input)} × ${formatNumber(sqft)} sq ft × ${days} days / 1000 = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'dollar_per_fte': {
            const boh = ctx.getNumeric(view, BOH_KEY, t) ?? 0;
            const foh = ctx.getNumeric(view, FOH_KEY, t) ?? 0;
            const hourly = boh + foh;
            const salaried =
                view === 'ga' ? (ctx.getNumeric(view, 'is.salaried_headcount', t) ?? 0) : 0;
            const fte = hourly > 0 ? hourly : salaried;
            const denomLabel =
                hourly > 0
                    ? `${formatNumber(fte)} FTEs (BOH ${formatNumber(boh)} + FOH ${formatNumber(foh)})`
                    : `${formatNumber(fte)} salaried headcount`;
            const formula = `${formatInput(driverType, input)} × ${denomLabel} = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'mom_pct': {
            const prev = ctx.getNumeric(view, lineKey, t - 1);
            const prevLabel = formatMonthLabel(monthKeyFromT(lastClosedMonth, t - 1));
            const formula = `${prevLabel} ${formatCurrency(prev)} × (1 + ${formatPercent(input)}) = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'mom_dollar': {
            const prev = ctx.getNumeric(view, lineKey, t - 1);
            const formula = `${formatCurrency(prev)} + ${formatCurrency(input)} = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'yoy_pct': {
            const prior = ctx.getNumeric(view, lineKey, t - 12);
            const priorLabel = formatMonthLabel(monthKeyFromT(lastClosedMonth, t - 12));
            const formula = `${priorLabel} ${formatCurrency(prior)} × (1 + ${formatPercent(input)}) = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'yoy_dollar': {
            const prior = ctx.getNumeric(view, lineKey, t - 12);
            const formula = `${formatCurrency(prior)} + ${formatCurrency(input)} = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'pct_gross_sales': {
            const gps = ctx.getNumeric(view, GPS_KEY, t);
            const formula = `${formatPercent(input)} × Gross Product Sales ${formatCurrency(gps)} = ${formatCurrency(computed)}`;
            return { formula, parts: [{ label: 'GPS', value_rendered: formatCurrency(gps) }] };
        }
        case 'pct_gross_sales_growth': {
            const gps = ctx.getNumeric(view, GPS_KEY, t);
            const gpsPrev = ctx.getNumeric(view, GPS_KEY, t - 1);
            const growth = gps != null && gpsPrev != null && gpsPrev !== 0 ? gps / gpsPrev - 1 : null;
            const formula = `${formatPercent(input)} × GPS growth ${formatPercent(growth)} = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'pct_of_line': {
            const ref = ctx.driverParams(view, lineKey)?.ref;
            const refLabel = ref ? schemaLabel(schema, ref) : 'reference';
            const refVal = ref ? ctx.getNumeric(view, ref, t) : null;
            const formula = `${formatPercent(input)} × ${refLabel} ${formatCurrency(refVal)} = ${formatCurrency(computed)}`;
            return { formula, parts: [{ label: refLabel, value_rendered: formatCurrency(refVal) }] };
        }
        case 'dollar_per_line_unit': {
            const ref = ctx.driverParams(view, lineKey)?.ref;
            const refLabel = ref ? schemaLabel(schema, ref) : 'reference';
            const refVal = ref ? ctx.getNumeric(view, ref, t) : null;
            const refEntry = ref ? schema.find((entry) => entry.key === ref) : null;
            const refRendered =
                refEntry?.unit === 'count' ? formatNumber(refVal) : formatCurrency(refVal);
            const formula = `${formatCurrency(input)} /unit × ${refLabel} ${refRendered} = ${formatCurrency(computed)}`;
            return { formula, parts: [{ label: refLabel, value_rendered: refRendered }] };
        }
        case 'dio':
        case 'dpo': {
            const cogs = ctx.getNumeric('consolidated', TOTAL_COGS_KEY, t);
            const days = ctx.daysInMonth(t);
            const formula = `${formatNumber(input)} days × consolidated COGS ${formatCurrency(cogs)} / ${days} days in month = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'dso': {
            const nr = ctx.getNumeric('consolidated', NET_REVENUE_KEY, t);
            const days = ctx.daysInMonth(t);
            const formula = `${formatNumber(input)} days × consolidated Net revenue ${formatCurrency(nr)} / ${days} days in month = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 's_curve': {
            const params = ctx.driverParams(view, lineKey);
            const formula = `S-curve start ${formatCurrency(params?.start)} → end ${formatCurrency(params?.end)}, midpoint ${params?.midpoint ?? '—'} = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'labor_buildup': {
            const payParams = parsePayPeriodParams(ctx.driverParams(view, lineKey));
            const mk = monthKeyFromT(lastClosedMonth, t);
            const payDates = payParams
                ? payDateListInMonth(payParams.cadence, payParams.anchor_date, mk).length
                : 0;
            const fteBoh = ctx.getNumeric(view, 'is.hourly_fte_boh', t);
            const fteFoh = ctx.getNumeric(view, 'is.hourly_fte_foh', t);
            const rateBoh = ctx.getNumeric(view, 'is.avg_hourly_rate_boh', t);
            const rateFoh = ctx.getNumeric(view, 'is.avg_hourly_rate_foh', t);
            const headcount = ctx.getNumeric(view, 'is.salaried_headcount', t);
            const salary = ctx.getNumeric(view, 'is.avg_salary_annual', t);
            const formula =
                `${formatNumber(fteBoh)} FTE BOH × ${formatCurrency(rateBoh)} × 80 h + ` +
                `${formatNumber(fteFoh)} FTE FOH × ${formatCurrency(rateFoh)} × 80 h + ` +
                `${formatNumber(headcount)} salaried × ${formatCurrency(salary)} ÷ 26, ` +
                `× ${payDates} pay dates = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'orders_x_ticket': {
            const orders = ctx.getNumeric(view, ORDERS_KEY, t);
            const ticket = ctx.getNumeric(view, TICKET_KEY, t);
            const formula = `Total Orders ${formatNumber(orders)} × Avg Ticket ${formatCurrency(ticket)} = ${formatCurrency(computed)}`;
            return { formula, parts: [] };
        }
        case 'linked':
            return { formula: 'Linked line', parts: [] };
        case 'plug':
            return { formula: 'Cash plug', parts: [] };
        case 'carry_forward': {
            const prior = ctx.getNumeric(view, lineKey, t - 1);
            const lastActual = ctx.getNumeric(view, lineKey, 0);
            const src = prior ?? lastActual;
            const formula = `Carry forward ${formatCurrency(src)}`;
            return { formula, parts: [] };
        }
        case 'ignore':
            return { formula: 'Ignored', parts: [] };
        default:
            return { formula: formatCurrency(computed), parts };
    }
}

export function explainCell(input: ExplainCellInput): ExplainModel {
    const { view, lineKey, t, schema, inputs, history, evaluated, notes, lastClosedMonth } =
        input;
    const entry = schema.find((e) => e.key === lineKey);
    const monthKey = monthKeyFromT(lastClosedMonth, t);
    const header = `${schemaLabel(schema, lineKey)} · ${VIEW_LABELS[view]} · ${formatMonthLabel(monthKey)}`;

    const lineState = inputs.drivers[view]?.[lineKey];
    let driverType = lineState?.active ?? null;
    if (!driverType) {
        const entry = schema.find((e) => e.key === lineKey);
        if (entry?.drivers?.length === 1) {
            driverType = entry.drivers[0] as DriverType;
        } else if (entry?.drivers?.includes('orders_x_ticket')) {
            driverType = 'orders_x_ticket';
        }
    }
    const labelNoteKey = labelCellKey(view, lineKey);
    const methodologyNoteKey = methodologyCellKey(view, lineKey);
    const activeCache = lineState?.active ? lineState.by_type[lineState.active] : null;
    const recipeObj = activeCache?.recipe ?? null;
    const rationale =
        recipeObj?.rationale?.trim() ||
        notes[methodologyNoteKey]?.trim() ||
        notes[labelNoteKey]?.trim() ||
        null;

    const cellNoteKey = projectedCellKey(view, lineKey, t);
    const noteList: { cell: string; text: string }[] = [];
    const cellNote = notes[cellNoteKey]?.trim();
    if (cellNote) {
        noteList.push({ cell: monthKey, text: cellNote });
    }

    if (!driverType) {
        return {
            header,
            computed: { formula: '—', parts: [] },
            recipe: null,
            context: null,
            rationale,
            notes: noteList,
        };
    }

    const ctx = buildCtx(input);
    const histView = historyView(view, lineKey, inputs);
    const computed = buildComputed(driverType, ctx, view, lineKey, t, lastClosedMonth, schema);

    if (FIXED_NO_CONTEXT.has(driverType)) {
        return {
            header,
            computed,
            recipe: null,
            context: null,
            rationale,
            notes: noteList,
        };
    }

    const schemaUnit = entry?.unit ?? 'usd';
    const showUsd = schemaUnit === 'usd';

    const context: ExplainModel['context'] = {
        last_actual: null,
        same_month_last_year: null,
        trailing_12: null,
        projected_12: null,
        rank_note: null,
    };

    if (history) {
        const lastActualInput = impliedDriverInput(driverType, ctx, histView, lineKey, 0);
        const lastActualUsd = getLineValue(evaluated, history, histView, lineKey, 0);
        const month0 = formatMonthLabel(monthKeyFromT(lastClosedMonth, 0));
        if (lastActualInput != null) {
            const line = formatContextLine(driverType, lastActualInput, lastActualUsd, showUsd);
            context.last_actual = line ? `${month0} ${line}` : null;
        } else if (showUsd && lastActualUsd != null) {
            context.last_actual = `${month0} ${formatCurrency(lastActualUsd)}`;
        }

        const smlyInput = impliedDriverInput(driverType, ctx, histView, lineKey, t - 12);
        const smlyUsd = getLineValue(evaluated, history, histView, lineKey, t - 12);
        const smlyLine = formatContextLine(driverType, smlyInput, smlyUsd, showUsd);
        const smlyMonth = formatMonthLabel(monthKeyFromT(lastClosedMonth, t - 12));
        if (smlyLine) {
            context.same_month_last_year = `${smlyMonth} ${smlyLine}`;
        } else if (showUsd && smlyUsd != null) {
            context.same_month_last_year = `${smlyMonth} ${formatCurrency(smlyUsd)}`;
        }

        const trailing = trailingImplied(ctx, driverType, histView, lineKey, history, lastClosedMonth);
        if (trailing != null) {
            context.trailing_12 = formatInput(driverType, trailing);
        } else if (showUsd) {
            const usdVals: number[] = [];
            for (let ht = -11; ht <= 0; ht += 1) {
                const v = getLineValue(evaluated, history, histView, lineKey, ht);
                if (v != null) usdVals.push(v);
            }
            if (usdVals.length > 0) {
                context.trailing_12 = formatCurrency(
                    usdVals.reduce((s, v) => s + v, 0) / usdVals.length,
                );
            }
        }

        const projVals: number[] = [];
        let projFromCache = false;
        for (let pt = 1; pt <= 12; pt += 1) {
            const fromCache = driverInputAt(inputs, view, lineKey, pt);
            if (fromCache != null) {
                projVals.push(fromCache);
                projFromCache = true;
                continue;
            }
            const fromEval = getLineValue(evaluated, history, view, lineKey, pt);
            if (showUsd && fromEval != null) {
                projVals.push(fromEval);
            }
        }
        if (projVals.length > 0) {
            const mean = projVals.reduce((s, v) => s + v, 0) / projVals.length;
            context.projected_12 = projFromCache
                ? formatInput(driverType, mean)
                : formatCurrency(mean);
        }

        context.rank_note = buildRankNote(history, histView, lineKey, lastClosedMonth, t);
    }

    let recipeBlock: ExplainRecipeBlock | null = null;
    if (recipeObj && driverType) {
        const projectionMonths = projectionMonthKeys(lastClosedMonth);
        const seasonalityResult = resolveSeasonalityFactors(recipeObj, {
            view,
            lineKey,
            driverType,
            params: activeCache?.params ?? {},
            history: history ?? undefined,
            schema,
            lastClosedMonth,
            pathPrefix: `drivers.${view}.${lineKey}.recipe`,
        });
        recipeBlock = explainRecipe(recipeObj, t, {
            lastClosedMonth,
            driverType,
            unit: driverInputUnit(driverType as Parameters<typeof driverInputUnit>[0]),
            seasonalityFactors: seasonalityResult.factors,
            projectionMonths,
        });
    }

    return {
        header,
        computed,
        recipe: recipeBlock,
        context,
        rationale,
        notes: noteList,
    };
}
